import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAccount, useConfig, useSwitchChain, useWalletClient } from 'wagmi'
import { getPublicClient } from '@wagmi/core'
import toast from 'react-hot-toast'
import { api, P2PContractConfig, P2PRelayerBalanceResponse, P2PRelayerBalanceSnapshot } from '../../lib/api'
import { wagmiChain } from '../../lib/p2p/tokens'
import { humanizeP2PError } from '../../lib/p2p/errors'

type Chain = 'BSC' | 'UNI2'
const CHAINS: Chain[] = ['BSC', 'UNI2']

/**
 * Admin surface for the P2PEscrowV2 contract on both chains. Reads
 * on-chain config via the backend, then sends setter calldata signed
 * by the owner's connected wallet. Backend never holds the owner key.
 */
export default function P2PContractConfigPage() {
  const { address } = useAccount()
  const { data: walletClient } = useWalletClient()
  const { switchChainAsync } = useSwitchChain()
  const wagmiConf = useConfig()
  const queryClient = useQueryClient()

  const bsc = useQuery<P2PContractConfig>({
    queryKey: ['p2p', 'admin', 'config', 'BSC'],
    queryFn: () => api.getP2PContractConfig('BSC'),
  })
  const uni2 = useQuery<P2PContractConfig>({
    queryKey: ['p2p', 'admin', 'config', 'UNI2'],
    queryFn: () => api.getP2PContractConfig('UNI2'),
  })

  // Shared: switch the wallet to `chain`, send the prebuilt calldata, wait
  // for the receipt. Used by every owner action (config setters + rescue).
  const sendOwnerTx = async (
    chain: Chain,
    calldata: { to: `0x${string}`; data: `0x${string}` },
    toastId: string,
  ): Promise<`0x${string}`> => {
    if (!walletClient) throw new Error('Conecta la wallet del owner primero')

    const targetChain = wagmiChain(chain)
    const publicClient = getPublicClient(wagmiConf, { chainId: targetChain.id })
    if (!publicClient) throw new Error(`No public client para ${chain}`)

    if (walletClient.chain?.id !== targetChain.id) {
      toast.loading(`Cambiando a ${chain}…`, { id: toastId })
      await switchChainAsync({ chainId: targetChain.id })
    }

    const hash = await walletClient.sendTransaction({
      to: calldata.to,
      data: calldata.data,
      value: 0n,
      chain: targetChain,
    })
    toast.loading(`Esperando confirmación (${hash.slice(0, 10)}…)`, { id: toastId })

    const receipt = await publicClient.waitForTransactionReceipt({ hash })
    if (receipt.status !== 'success') throw new Error('Tx revertida on-chain')
    return hash
  }

  const dispatch = useMutation({
    mutationFn: async (vars: { chain: Chain; endpoint: string; payload: Record<string, unknown> }) => {
      toast.loading(`Firmando tx en ${vars.chain}…`, { id: 'p2p-admin-tx' })
      const calldata = await api.buildP2PAdminCalldata(vars.endpoint, {
        ...vars.payload,
        chain: vars.chain,
      })
      return sendOwnerTx(vars.chain, calldata, 'p2p-admin-tx')
    },
    onSuccess: (hash) => {
      toast.success(`Confirmada: ${hash.slice(0, 10)}…`, { id: 'p2p-admin-tx' })
      queryClient.invalidateQueries({ queryKey: ['p2p', 'admin', 'config'] })
    },
    onError: (err) => {
      toast.error(humanizeP2PError(err), { id: 'p2p-admin-tx' })
    },
  })

  // Rescue a one-sided / stuck order: emergencyWithdrawTo requires the
  // contract to be paused, so this runs pause → emergencyWithdrawTo → unpause
  // as 3 owner-signed txs (skipping the pause/unpause wrappers if the
  // contract is already paused, to respect a deliberate operator pause).
  const rescue = useMutation({
    mutationFn: async (vars: {
      chain: Chain
      orderId: string
      recipient: string
      alreadyPaused: boolean
    }) => {
      const steps: { label: string; build: () => Promise<{ to: `0x${string}`; data: `0x${string}` }> }[] = []
      if (!vars.alreadyPaused) {
        steps.push({ label: 'pause', build: () => api.buildP2PAdminCalldata('pause', { chain: vars.chain }) })
      }
      steps.push({
        label: 'emergencyWithdrawTo',
        build: () => api.buildP2PEmergencyWithdraw(vars.orderId, vars.chain, vars.recipient),
      })
      if (!vars.alreadyPaused) {
        steps.push({ label: 'unpause', build: () => api.buildP2PAdminCalldata('unpause', { chain: vars.chain }) })
      }

      let last = '' as `0x${string}`
      for (const step of steps) {
        toast.loading(`Firmando ${step.label} en ${vars.chain}…`, { id: 'p2p-rescue' })
        const calldata = await step.build()
        last = await sendOwnerTx(vars.chain, calldata, 'p2p-rescue')
        toast.loading(`${step.label} confirmada (${last.slice(0, 10)}…)`, { id: 'p2p-rescue' })
      }
      return last
    },
    onSuccess: (hash) => {
      toast.success(`Rescate completado: ${hash.slice(0, 10)}…`, { id: 'p2p-rescue' })
      queryClient.invalidateQueries({ queryKey: ['p2p', 'admin', 'config'] })
    },
    onError: (err) => {
      toast.error(humanizeP2PError(err), { id: 'p2p-rescue' })
    },
  })

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-bold text-white">P2P Contract Config</h1>
        <p className="text-sm text-dark-400 mt-1">
          Setters del owner del P2PEscrowV2 en BSC y UNI2. Cada cambio se firma con tu wallet conectada.
        </p>
      </header>

      {!address && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-amber-300 text-sm">
          Conecta la wallet del owner para firmar cambios.
        </div>
      )}

      <RelayerBalancePanel />

      {CHAINS.map((chain) => {
        const q = chain === 'BSC' ? bsc : uni2
        return (
          <ChainCard
            key={chain}
            chain={chain}
            data={q.data}
            loading={q.isLoading}
            pending={dispatch.isPending}
            onDispatch={(endpoint, payload) => dispatch.mutate({ chain, endpoint, payload })}
          />
        )
      })}

      <RescueCard
        bscPaused={!!bsc.data?.paused}
        uni2Paused={!!uni2.data?.paused}
        pending={rescue.isPending}
        onRescue={(vars) => rescue.mutate(vars)}
      />
    </div>
  )
}

/**
 * Emergency rescue for a one-sided / stuck order. Sends, signed by the owner,
 * pause → emergencyWithdrawTo(orderId, recipient) → unpause on the chosen
 * escrow, redirecting the unfilled remainder to `recipient`. Pre-filled with
 * the 2026-05-28 incident (taker order on UNI2 → user 0x4BC7…); editable for
 * any future case.
 */
function RescueCard({
  bscPaused,
  uni2Paused,
  pending,
  onRescue,
}: {
  bscPaused: boolean
  uni2Paused: boolean
  pending: boolean
  onRescue: (vars: { chain: Chain; orderId: string; recipient: string; alreadyPaused: boolean }) => void
}) {
  const [chain, setChain] = useState<Chain>('UNI2')
  const [orderId, setOrderId] = useState(
    '0x458f2c06c7a4667dae875f44a27fd27f432ffae6ad87c46e3cb24d2fff338715',
  )
  const [recipient, setRecipient] = useState('0x4BC7FafaD38554C992b53cDEef45504979D6c78C')

  const alreadyPaused = chain === 'BSC' ? bscPaused : uni2Paused
  const orderIdOk = /^0x[0-9a-fA-F]{64}$/.test(orderId.trim())
  const recipientOk = /^0x[0-9a-fA-F]{40}$/.test(recipient.trim())
  const canRun = orderIdOk && recipientOk && !pending

  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-red-300">Rescate de orden (emergencyWithdrawTo)</h2>
        <p className="text-xs text-dark-400 mt-1">
          Libera el remanente sin liquidar de una orden a una dirección alternativa. Owner-only, on-chain.
          Ejecuta <span className="font-mono">pause → emergencyWithdrawTo → unpause</span> (3 firmas).
          {alreadyPaused && ' El contrato ya está PAUSED: solo se firmará emergencyWithdrawTo.'}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-dark-300">
          Cadena
          <select
            value={chain}
            onChange={(e) => setChain(e.target.value as Chain)}
            className="mt-1 w-full rounded-md bg-dark-800 border border-dark-600 px-2 py-1.5 text-sm text-white"
          >
            {CHAINS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-dark-300">
          Destinatario (recipient)
          <input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            spellCheck={false}
            className={`mt-1 w-full rounded-md bg-dark-800 border px-2 py-1.5 text-sm font-mono text-white ${
              recipientOk ? 'border-dark-600' : 'border-red-500/60'
            }`}
          />
        </label>
        <label className="text-xs text-dark-300 sm:col-span-2">
          Order ID (bytes32)
          <input
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            spellCheck={false}
            className={`mt-1 w-full rounded-md bg-dark-800 border px-2 py-1.5 text-sm font-mono text-white ${
              orderIdOk ? 'border-dark-600' : 'border-red-500/60'
            }`}
          />
        </label>
      </div>

      <button
        onClick={() =>
          onRescue({ chain, orderId: orderId.trim(), recipient: recipient.trim(), alreadyPaused })
        }
        disabled={!canRun}
        className="w-full rounded-md bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 text-sm font-semibold text-white"
      >
        {pending ? 'Ejecutando rescate…' : 'Rescatar (firmar 3 txs)'}
      </button>
    </div>
  )
}

function ChainCard({
  chain,
  data,
  loading,
  pending,
  onDispatch,
}: {
  chain: Chain
  data: P2PContractConfig | undefined
  loading: boolean
  pending: boolean
  onDispatch: (endpoint: string, payload: Record<string, unknown>) => void
}) {
  if (loading) return <div className="text-dark-400 text-sm">Cargando {chain}…</div>
  if (!data?.enabled) {
    return (
      <div className="rounded-lg border border-dark-700 bg-dark-900/40 p-4">
        <div className="text-white font-mono">{chain}</div>
        <div className="text-dark-400 text-sm mt-1">Escrow no configurado en esta chain.</div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-dark-700 bg-dark-900/40 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-white font-mono text-lg">{chain}</div>
          <div className="text-[11px] text-dark-500 font-mono break-all mt-1">{data.address}</div>
        </div>
        <span
          className={
            data.paused
              ? 'text-xs px-2 py-1 rounded bg-rose-500/20 text-rose-300'
              : 'text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-300'
          }
        >
          {data.paused ? 'PAUSED' : 'ACTIVE'}
        </span>
      </div>

      <ReadOnly label="Relayer" value={data.relayer} />
      <ReadOnly label="Treasury" value={data.treasury} />
      <ReadOnly label="Token" value={data.token} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <NumberField
          label="platformFeeBps (0–500, 100 = 1%)"
          initial={String(data.platformFeeBps ?? 0)}
          pending={pending}
          onSubmit={(v) => onDispatch('fee', { value: Number(v) })}
        />
        <WeiField
          label={`gasFeeFlat (wei del token, hard-cap 5 × 10¹⁸)`}
          initial={data.gasFeeFlat ?? '0'}
          pending={pending}
          onSubmit={(v) => onDispatch('gas-fee-flat', { value: v })}
        />
        <NumberField
          label="takeMaxTtlSeconds (1–600)"
          initial={String(data.takeMaxTtlSeconds ?? 0)}
          pending={pending}
          onSubmit={(v) => onDispatch('take-max-ttl', { value: Number(v) })}
        />
        <WeiField
          label="minOrderAmount (wei)"
          initial={data.minOrderAmount ?? '0'}
          pending={pending}
          onSubmit={(v) => onDispatch('min-order', { value: v })}
        />
        <NumberField
          label="maxPremiumBps (0–5000, 1000 = 10%)"
          initial={String(data.maxPremiumBps ?? 0)}
          pending={pending}
          onSubmit={(v) => onDispatch('max-premium', { value: Number(v) })}
        />
        <NumberField
          label="maxExpirationSeconds (max 90 días)"
          initial={String(data.maxExpirationSeconds ?? 0)}
          pending={pending}
          onSubmit={(v) => onDispatch('max-expiration', { value: Number(v) })}
        />
        <WeiField
          label="maxVolumePer24h (wei)"
          initial={data.maxVolumePer24h ?? '0'}
          pending={pending}
          onSubmit={(v) => onDispatch('max-volume', { value: v })}
        />
        <div>
          <div className="text-xs text-dark-500 mb-1">availableWindowCapacity (wei, read-only)</div>
          <div className="font-mono text-xs text-dark-300 break-all px-2 py-1.5 rounded bg-dark-950 border border-dark-700">
            {data.availableWindowCapacity ?? '0'}
          </div>
        </div>
      </div>

      <div className="pt-3 border-t border-dark-800 space-y-3">
        <FeesWithdrawSection
          accrued={data.feesAccrued ?? '0'}
          pending={pending}
          onWithdraw={(amount) => onDispatch('withdraw-fees', { value: amount })}
        />
        <button
          type="button"
          disabled={pending}
          onClick={() => onDispatch(data.paused ? 'unpause' : 'pause', {})}
          className={
            data.paused
              ? 'px-4 py-2 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-sm border border-emerald-500/30 disabled:opacity-30'
              : 'px-4 py-2 rounded bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 text-sm border border-rose-500/30 disabled:opacity-30'
          }
        >
          {data.paused ? 'Unpause' : 'Pause'}
        </button>
      </div>
    </div>
  )
}

function ReadOnly({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <div className="text-xs text-dark-500 mb-1">{label}</div>
      <div className="font-mono text-xs text-dark-300 break-all px-2 py-1.5 rounded bg-dark-950 border border-dark-700">
        {value ?? '—'}
      </div>
    </div>
  )
}

function NumberField({
  label,
  initial,
  pending,
  onSubmit,
}: {
  label: string
  initial: string
  pending: boolean
  onSubmit: (v: string) => void
}) {
  const [v, setV] = useState(initial)
  const dirty = v !== initial
  return (
    <div>
      <div className="text-xs text-dark-500 mb-1">{label}</div>
      <div className="flex gap-2">
        <input
          className="flex-1 min-w-0 bg-dark-950 border border-dark-700 rounded px-2 py-1.5 text-sm text-white font-mono"
          value={v}
          onChange={(e) => setV(e.target.value)}
        />
        <button
          type="button"
          disabled={pending || !dirty}
          onClick={() => onSubmit(v)}
          className="px-3 py-1.5 rounded bg-uni-500/20 hover:bg-uni-500/30 text-uni-300 text-sm border border-uni-500/30 disabled:opacity-30"
        >
          Set
        </button>
      </div>
    </div>
  )
}

function WeiField(props: React.ComponentProps<typeof NumberField>) {
  return <NumberField {...props} />
}

function FeesWithdrawSection({
  accrued,
  pending,
  onWithdraw,
}: {
  accrued: string
  pending: boolean
  onWithdraw: (amount: string) => void
}) {
  const [amount, setAmount] = useState('')
  const accruedBig = (() => { try { return BigInt(accrued) } catch { return 0n } })()
  return (
    <div className="rounded-lg bg-dark-950 border border-dark-700 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-dark-500">feesAccrued (wei)</div>
          <div className="font-mono text-sm text-emerald-300 break-all">{accrued}</div>
        </div>
        <button
          type="button"
          disabled={pending || accruedBig === 0n}
          onClick={() => onWithdraw(accrued)}
          className="px-3 py-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-sm border border-emerald-500/30 disabled:opacity-30"
        >
          Withdraw todo al treasury
        </button>
      </div>
      <div className="flex gap-2">
        <input
          className="flex-1 min-w-0 bg-dark-900 border border-dark-700 rounded px-2 py-1.5 text-sm text-white font-mono"
          placeholder="amount en wei (parcial)"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <button
          type="button"
          disabled={pending || amount === '' || amount === '0'}
          onClick={() => onWithdraw(amount)}
          className="px-3 py-1.5 rounded bg-uni-500/20 hover:bg-uni-500/30 text-uni-300 text-sm border border-uni-500/30 disabled:opacity-30"
        >
          Withdraw parcial
        </button>
      </div>
    </div>
  )
}

// ============================================================================
//  Relayer balances panel
// ============================================================================

const EXPLORER_BY_CHAIN: Record<'BSC' | 'UNI2', { name: string; addressUrl: (addr: string) => string }> = {
  BSC: {
    name: 'BscScan',
    addressUrl: (a) => `https://bscscan.com/address/${a}`,
  },
  UNI2: {
    name: 'Wer2scan',
    addressUrl: (a) => `https://main.wer2scan.com/address/${a}`,
  },
}

function RelayerBalancePanel() {
  const queryClient = useQueryClient()
  const q = useQuery<P2PRelayerBalanceResponse>({
    queryKey: ['p2p', 'admin', 'relayer-balance'],
    queryFn: () => api.getP2PRelayerBalances(),
    // Backend refreshes against chain on each call; auto-refetch every 30s
    // so an idle admin tab still notices a drained relayer reasonably fast.
    refetchInterval: 30_000,
  })

  const runExpiration = useMutation({
    mutationFn: () => api.runP2PExpiration(),
    onSuccess: (r) => {
      toast.success(
        r.skipped
          ? 'Expiration sweep skipped (otra instancia ya está corriendo)'
          : `Expiration sweep ejecutada — ${r.attempted} orden(es) procesadas`,
      )
      queryClient.invalidateQueries({ queryKey: ['p2p'] })
    },
    onError: (e) => toast.error(humanizeP2PError(e)),
  })

  return (
    <div className="rounded-lg border border-dark-700 bg-dark-900/40 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-mono">Relayer gas balances</h2>
          <p className="text-[11px] text-dark-500 mt-0.5">
            EOA que firma <code>executeTrades</code> y <code>expireOrder</code>. Si baja del threshold,
            la liquidación se cuelga silenciosamente — fondea desde el explorer.
          </p>
        </div>
        <button
          type="button"
          onClick={() => runExpiration.mutate()}
          disabled={runExpiration.isPending}
          className="px-3 py-1.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-xs border border-amber-500/30 disabled:opacity-30"
          title="Procesa todas las órdenes expiradas pendientes (refund) sin esperar el cron de 5 min"
        >
          {runExpiration.isPending ? 'Procesando…' : 'Procesar expirados ahora'}
        </button>
      </div>

      {q.isLoading && <div className="text-dark-400 text-sm">Consultando saldos…</div>}
      {q.error && (
        <div className="rounded border border-rose-500/30 bg-rose-500/10 p-2 text-rose-300 text-xs">
          Error: {humanizeP2PError(q.error)}
        </div>
      )}

      {q.data && (
        <div className="space-y-2">
          {q.data.relayers.map((r) => (
            <RelayerRow key={r.chain} snap={r} />
          ))}
          {q.data.relayers.length === 0 && (
            <div className="text-dark-500 text-xs">Ningún relayer configurado.</div>
          )}
          <div
            className={
              q.data.summary.healthy
                ? 'text-[11px] text-emerald-400'
                : q.data.summary.anyEmpty
                ? 'text-[11px] text-rose-400'
                : 'text-[11px] text-amber-400'
            }
          >
            {q.data.summary.healthy
              ? '✓ Todos los relayers sobre el threshold'
              : q.data.summary.anyEmpty
              ? '✗ Al menos un relayer está VACÍO — la liquidación está parada'
              : '! Al menos un relayer bajo del threshold — fondear pronto'}
          </div>
        </div>
      )}
    </div>
  )
}

function RelayerRow({ snap }: { snap: P2PRelayerBalanceSnapshot }) {
  const explorer = EXPLORER_BY_CHAIN[snap.chain]
  const dotClass = snap.empty
    ? 'bg-rose-400 animate-pulse'
    : snap.low
    ? 'bg-amber-400 animate-pulse'
    : 'bg-emerald-400'
  const balanceClass = snap.empty
    ? 'text-rose-300'
    : snap.low
    ? 'text-amber-300'
    : 'text-emerald-300'
  return (
    <div className="rounded border border-dark-800 bg-dark-950/40 p-3 flex items-center gap-3">
      <span className={`w-2 h-2 rounded-full shrink-0 ${dotClass}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-dark-300">{snap.chain}</span>
          <a
            href={explorer.addressUrl(snap.address)}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] font-mono text-uni-400 hover:text-uni-300 truncate"
            title={snap.address}
          >
            {snap.address}
          </a>
        </div>
        <div className="text-[11px] text-dark-500 mt-0.5">
          Threshold: {formatNative(snap.thresholdEther)} · Última lectura: {new Date(snap.checkedAt).toLocaleTimeString()}
        </div>
      </div>
      <div className={`font-mono text-sm tabular-nums shrink-0 ${balanceClass}`}>
        {formatNative(snap.balanceEther)}
      </div>
    </div>
  )
}

function formatNative(ether: string): string {
  const n = Number(ether)
  if (!Number.isFinite(n)) return ether
  if (n === 0) return '0'
  if (n < 0.0001) return n.toExponential(2)
  return n.toFixed(n >= 1 ? 3 : 6)
}
